// client/src/components/LeadComposeEmailModal.js
// @@LEAD_COMPOSE_EMAIL_MODAL_v8@@ — applied by v8-leads-compose-email.sh
// Sends the lead's first email FROM the logged-in user's own mailbox.
// The From line is display-only; the server forces From = req.user.email.
import React, { useState } from 'react';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';

export default function LeadComposeEmailModal({ lead, onClose, onSent }) {
  const { user } = useAuth();

  const recipientName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim();
  const greeting = (lead.first_name || '').trim() || 'there';

  const [toEmail, setToEmail] = useState(lead.email || '');
  const [subject, setSubject] = useState(lead.company ? `Hello from XYZ Merchandise` : '');
  const [body, setBody] = useState(`Hi ${greeting},\n\n`);
  const [includeSignature, setIncludeSignature] = useState(true);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');

  const fromName = user
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email
    : 'You';
  const fromEmail = user ? user.email : '';

  async function send() {
    setErr('');
    if (!toEmail.trim()) { setErr('Recipient email is required.'); return; }
    if (!subject.trim()) { setErr('Subject is required.'); return; }
    if (!body.trim()) { setErr('Message is required.'); return; }
    setSending(true);
    try {
      const res = await apiFetch(`/api/leads/${lead.id}/emails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_email: toEmail.trim(),
          to_name: recipientName || null,
          subject: subject.trim(),
          body,
          include_signature: includeSignature,
        }),
      });
      if (res.ok) {
        onSent && onSent();
        onClose && onClose();
      } else {
        const d = await res.json().catch(() => ({}));
        setErr(d.error || 'Could not send the email.');
      }
    } catch (e) {
      setErr('Could not reach the server. Try again.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="hq-modal-backdrop" onClick={onClose}>
      <div className="hq-modal" onClick={(e) => e.stopPropagation()}>
        <div className="hq-modal-head">
          <h2>Compose email</h2>
          <button className="hq-modal-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="hq-modal-body">
          {err && <p className="hq-msg" style={{ color: '#ff7a73' }}>{err}</p>}

          <label className="hq-modal-label">From</label>
          <div className="hq-modal-from">{fromName} &lt;{fromEmail}&gt;</div>
          <p className="hq-modal-hint">Sends from your mailbox; replies come straight back to you.</p>

          <label className="hq-modal-label">To</label>
          <input className="hq-input" style={{ width: '100%' }} type="email" value={toEmail}
            onChange={(e) => setToEmail(e.target.value)} placeholder="recipient@example.com" />

          <label className="hq-modal-label">Subject</label>
          <input className="hq-input" style={{ width: '100%' }} value={subject}
            onChange={(e) => setSubject(e.target.value)} placeholder="Subject" />

          <label className="hq-modal-label">Message</label>
          <textarea className="hq-input" rows={9} value={body}
            onChange={(e) => setBody(e.target.value)}
            style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', padding: 12 }} />
          <p className="hq-modal-hint">Plain text — line breaks are preserved.</p>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeSignature}
              onChange={(e) => setIncludeSignature(e.target.checked)} />
            <span>Include my signature</span>
          </label>

          <p className="hq-modal-hint" style={{ marginTop: 12 }}>
            A 1×1 tracking pixel is added so opens show under Open tracking. Note
            that many mail clients prefetch images (Apple Mail Privacy, Gmail
            proxy, Outlook scanning), which can register as opens the recipient
            never made — treat counts as "delivered, maybe read."
          </p>
        </div>
        <div className="hq-modal-foot">
          <button className="hq-signout" style={{ width: 'auto', padding: '0 16px' }} onClick={onClose}>Cancel</button>
          <button className="hq-btn" onClick={send} disabled={sending}>
            {sending ? 'Sending…' : 'Send email'}
          </button>
        </div>
      </div>
    </div>
  );
}
